/**
 * PR 5 (A6, D9) — saved bottom tabs for the old PPC pages become ONE
 * Advertising tab. `loadTabs()` replays `clickpay.tabs.v1` as saved, so an
 * operator with Campaigns + Targets + Placements pills would keep three pills
 * that now all render the hub. Pure; runs once per load.
 */
import { legacyAdsHref } from '../pages/ads/legacyRoutes'
import { ADS_PATH } from '../pages/ads/routing'
import { deriveTabKey, deriveTabTitle } from './tabIdentity'
import type { TabRecord } from './types'

/** Rewrite every old-PPC pill onto /ads, then collapse all /ads pills into
 *  one at the first such slot: the most recently active location wins, the
 *  newest activity time is kept, pinned if any was. Returns the SAME array
 *  when there is nothing to migrate or merge. */
export function migrateLegacyAdsTabs(tabs: readonly TabRecord[]): TabRecord[] {
  let changed = false
  const rewritten = tabs.map(t => {
    const to = legacyAdsHref(t.location.pathname, t.location.search)
    if (!to) return t
    changed = true
    // Not a hub URL (e.g. /ads/overview -> /reports/ads-overview): the pill
    // keeps its own query and moves onto the real page, never into the hub.
    if (!to.startsWith(`${ADS_PATH}?`)) {
      const q = to.indexOf('?')
      const pathname = q < 0 ? to : to.slice(0, q)
      const search = q < 0 ? '' : to.slice(q)
      return {
        ...t,
        id: deriveTabKey(pathname, search),
        title: deriveTabTitle(pathname, search),
        location: { pathname, search, hash: t.location.hash },
      }
    }
    const search = to.slice(to.indexOf('?'))
    return {
      ...t,
      id: deriveTabKey(ADS_PATH, search),
      title: deriveTabTitle(ADS_PATH, search),
      location: { pathname: ADS_PATH, search, hash: t.location.hash },
    }
  })
  const adsKey = deriveTabKey(ADS_PATH, '')
  if (rewritten.filter(t => t.id === adsKey).length < 2) return changed ? rewritten : (tabs as TabRecord[])

  const out: TabRecord[] = []
  let kept: TabRecord | null = null
  let slot = -1
  for (const t of rewritten) {
    if (t.id !== adsKey) { out.push(t); continue }
    if (!kept) {
      kept = t
      slot = out.length
      out.push(t)
      continue
    }
    // Later duplicates fold into the first slot; the newer activity wins.
    const newer: TabRecord = t.lastActiveAt > kept.lastActiveAt ? t : kept
    kept = {
      ...kept,
      location: newer.location,
      title: deriveTabTitle(newer.location.pathname, newer.location.search),
      lastActiveAt: newer.lastActiveAt,
      pinned: kept.pinned || t.pinned,
    }
    out[slot] = kept
  }
  return out
}
